import { ReactNode, useContext, useEffect, useState, useCallback } from 'react';
import { createContext } from 'react';

import { DashboardTabsKeysEnum } from '../model/dashboard-tabs';
import { SourceTypeEnum } from '../model/source_type.enum';
import { ERROR_TYPE, ImmersiveError } from '../utils/error';
import { getStorage, setStorage } from '../utils/storage_service';

import { GlobalContext } from './global-provider';

interface IDashboardParamsProvider {
  children?: ReactNode;
}

interface IDashboardProvider {
  tabs: { key: string; label: string }[];
  addTab: (key?: string, label?: string) => void;
  renameTab: (key: string, newName: string) => void;
  deleteTab: (tabKey: string) => void;
  closeTab: (tabKey: string) => void;
  selectTab: (key: DashboardTabsKeysEnum) => void;
  source: SourceTypeEnum;
  setSource: (props: SourceTypeEnum) => void;
  error: ImmersiveError | null;
  handleError: (name: ERROR_TYPE, message: string, cause?: any) => void;
  clearError: () => void;
  newTab: string;
  setNewTab: React.Dispatch<React.SetStateAction<string>>;
  createTabKey: (label: string) => string;
  createTabName: () => string;
  duplicateTab: (tabKey: string, newLabel?: string) => void;
  setTabsOrder?: (order: string[]) => void;
}

export const DashboardContext = createContext({} as IDashboardProvider);

export const DashboardProvider = (props: IDashboardParamsProvider) => {
  const { setMarketDataOverviewCurrentTab, marketDataOverviewCurrentTab } =
    useContext(GlobalContext);
  const [source, setSource] = useState<SourceTypeEnum>(SourceTypeEnum.DERIBIT);
  const [newTab, setNewTab] = useState('');
  const DEFAULT_TABS: { key: string; label: string }[] = [
    { key: DashboardTabsKeysEnum.overviewBTC, label: 'BTC Overview' },
    { key: DashboardTabsKeysEnum.volLab, label: 'Vol Lab' },
    { key: DashboardTabsKeysEnum.altSurfaceBuilder, label: 'Alt Surface Builder' },
    { key: DashboardTabsKeysEnum.marketChange, label: 'Market Change' },
  ];

  const [tabs, setTabs] = useState<{ key: string; label: string }[]>(DEFAULT_TABS);
  const [customOrder, setCustomOrder] = useState<string[] | null>(null);
  const [error, setError] = useState<ImmersiveError | null>(null);

  const handleError = (name: ERROR_TYPE, message: string, cause?: any) => {
    setError({ name, message, cause });
  };

  const clearError = () => {
    setError(null);
  };

  const createTabKey = (_label: string) => {
    let key: string;
    const tabKeys = tabs.map((tab) => tab.key);
    do {
      key = `custom_tab_${Math.random().toString(36).substr(2, 9)}`;
    } while (tabKeys.includes(key));
    return key;
  };

  const createTabName = () => {
    const defaultName = 'New Dashboard';
    let newName = defaultName;
    let counter = 1;
    const tabNames = tabs.map((tab) => tab.label);
    while (tabNames.includes(newName)) {
      newName = `${defaultName} ${counter}`;
      counter++;
    }
    return newName;
  };

  const applyCustomOrder = useCallback(
    (incoming: { key: string; label: string }[], order: string[] | null) => {
      if (!order || order.length === 0) return incoming;
      const map = new Map(incoming.map((t) => [t.key, t]));
      const ordered: { key: string; label: string }[] = [];
      order.forEach((k) => {
        const item = map.get(k);
        if (item) {
          ordered.push(item);
          map.delete(k);
        }
      });
      map.forEach((value) => ordered.push(value));
      return ordered;
    },
    [],
  );

  const persistOrder = useCallback((nextTabs: { key: string; label: string }[]) => {
    const keys = nextTabs.map((t) => t.key);
    setStorage('TABS_ORDER', keys);
    setCustomOrder(keys);
  }, []);

  const addTab = (key?: string, label?: string) => {
    const tabLabel = label ?? createTabName();
    const tabKey = key ?? createTabKey(tabLabel);

    setTabs((prevTabs) => {
      const tabExists = prevTabs.some((tab) => tab.key === tabKey);
      if (!tabExists) {
        const graphGalleryIndex = prevTabs.findIndex(
          (tab) => tab.key === DashboardTabsKeysEnum.volLab,
        );

        const newTabs = [...prevTabs];
        const newTab = { key: tabKey, label: tabLabel };
        if (graphGalleryIndex === -1) {
          newTabs.push(newTab);
        } else {
          newTabs.splice(graphGalleryIndex, 0, newTab);
        }
        const applied = applyCustomOrder(newTabs, customOrder);
        persistOrder(applied);
        return applied;
      }
      return prevTabs;
    });

    setMarketDataOverviewCurrentTab(tabKey);
  };

  const deleteTab = (key: string) => {
    const currentTabIndex = tabs.findIndex((tab) => tab.key === key);

    if (currentTabIndex > 1) {
      setMarketDataOverviewCurrentTab(tabs[currentTabIndex - 1].key);
    } else {
      setMarketDataOverviewCurrentTab(tabs[0].key);
    }

    setTabs((prev) => {
      const filtered = prev.filter((tab) => tab.key !== key);
      persistOrder(filtered);
      return filtered;
    });
  };

  const renameTab = (key: string, newName: string) => {
    setTabs((prev) => prev.map((tab) => (tab.key === key ? { ...tab, label: newName } : tab)));
  };

  const closeTab = (tabKey: string) => {
    setTabs((prevTabs) => {
      const tabIndex = prevTabs.findIndex((tab) => tab.key === tabKey);
      const newTabs = prevTabs.filter((tab) => tab.key !== tabKey);
      if (marketDataOverviewCurrentTab === tabKey) {
        const newCurrentTab = tabIndex > 0 ? newTabs[tabIndex - 1].key : newTabs[0]?.key;
        if (newCurrentTab) setMarketDataOverviewCurrentTab(newCurrentTab);
      }
      persistOrder(newTabs);
      return newTabs;
    });
  };

  const selectTab = (key: DashboardTabsKeysEnum) => {
    setMarketDataOverviewCurrentTab(key);
  };

  const duplicateTab = (tabKey: string, newLabel?: string) => {
    const storagedData = getStorage(`WIDGETS_${tabKey}`);
    const label = newLabel ?? createTabName();
    const newKey = createTabKey(label);

    addTab(newKey, label);

    if (storagedData) {
      setStorage(`WIDGETS_${newKey}`, storagedData);
    }
  };

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [marketDataOverviewCurrentTab]);

  useEffect(() => {
    const savedTabs = getStorage('TABS');
    const savedOrder = getStorage('TABS_ORDER');

    const initialTabs: { key: string; label: string }[] =
      savedTabs && Array.isArray(savedTabs) ? savedTabs : DEFAULT_TABS;

    const isGraphGalleryLabel = (label: string) => /graph\s*gallery/i.test(label || '');
    const normalizeOverviewLabel = (label: string, key: string) => {
      if (!label) return label;
      const m = label.match(/^\s*(BTC|ETH|SOL|XRP)\s*-\s*USDC\s+Overview\s*$/i);
      if (m) return `${m[1].toUpperCase()} Overview`;

      if (key?.startsWith('overview_')) {
        const token = key.split('_')[1]?.toUpperCase();
        if (['BTC', 'ETH', 'SOL', 'XRP'].includes(token || '')) {
          const re = new RegExp(`^\\s*${token}\\s*-\\s*USDC\\s+Overview\\s*$`, 'i');
          if (re.test(label)) return `${token} Overview`;
        }
      }
      return label;
    };
    const mapOldKeyToNew = (key: string, label: string) => {
      if (!key) return key;
      const lower = String(key).toLowerCase();
      if (lower === 'graph_galley' || lower === 'graph_gallery' || lower === 'compare') {
        return DashboardTabsKeysEnum.volLab;
      }
      if (isGraphGalleryLabel(label)) return DashboardTabsKeysEnum.volLab;
      return key;
    };

    const seen = new Set<string>();
    const migratedTabs: { key: string; label: string }[] = [];
    for (const t of initialTabs) {
      const nextKey = mapOldKeyToNew(t.key, t.label);
      const nextLabel =
        nextKey === DashboardTabsKeysEnum.volLab || isGraphGalleryLabel(t.label)
          ? 'Vol Lab'
          : normalizeOverviewLabel(t.label, nextKey);
      if (!seen.has(nextKey)) {
        seen.add(nextKey);
        migratedTabs.push({ key: nextKey, label: nextLabel });
      }
    }

    if (!migratedTabs.some((t) => t.key === DashboardTabsKeysEnum.volLab)) {
      migratedTabs.push({ key: DashboardTabsKeysEnum.volLab, label: 'Vol Lab' });
    }

    if (!migratedTabs.some((t) => t.key === DashboardTabsKeysEnum.altSurfaceBuilder)) {
      migratedTabs.push({
        key: DashboardTabsKeysEnum.altSurfaceBuilder,
        label: 'Alt Surface Builder',
      });
    }

    if (!migratedTabs.some((t) => t.key === DashboardTabsKeysEnum.marketChange)) {
      migratedTabs.push({
        key: DashboardTabsKeysEnum.marketChange,
        label: 'Market Change',
      });
    }

    let migratedOrder: string[] | null = null;
    if (Array.isArray(savedOrder)) {
      const orderSeen = new Set<string>();
      migratedOrder = savedOrder
        .map((k: string) => mapOldKeyToNew(k, ''))
        .filter((k: string) => {
          if (orderSeen.has(k)) return false;
          orderSeen.add(k);
          return migratedTabs.some((t) => t.key === k);
        });
    }

    const didTabsChange =
      initialTabs.length !== migratedTabs.length ||
      initialTabs.some(
        (t, i) => t.key !== migratedTabs[i]?.key || t.label !== migratedTabs[i]?.label,
      );
    const didOrderChange = Array.isArray(savedOrder)
      ? !migratedOrder || savedOrder.join('|') !== migratedOrder.join('|')
      : false;
    if (didTabsChange) setStorage('TABS', migratedTabs);
    if (didOrderChange && migratedOrder) setStorage('TABS_ORDER', migratedOrder);

    setCustomOrder(migratedOrder ?? null);
    setTabs(applyCustomOrder(migratedTabs, migratedOrder ?? null));
  }, [applyCustomOrder]);

  useEffect(() => {
    setStorage('TABS', tabs);
  }, [tabs]);

  const setTabsOrder = useCallback(
    (orderedKeys: string[]) => {
      setTabs((prev) => {
        const map = new Map(prev.map((t) => [t.key, t]));
        const reordered: { key: string; label: string }[] = [];
        orderedKeys.forEach((k) => {
          const item = map.get(k);
          if (item) {
            reordered.push(item);
            map.delete(k);
          }
        });
        map.forEach((value) => reordered.push(value));
        persistOrder(reordered);
        return reordered;
      });
    },
    [persistOrder],
  );

  return (
    <DashboardContext.Provider
      value={{
        tabs,
        addTab,
        renameTab,
        deleteTab,
        closeTab,
        selectTab,
        source,
        setSource,
        error,
        handleError,
        clearError,
        newTab,
        setNewTab,
        createTabKey,
        createTabName,
        duplicateTab,
        setTabsOrder,
      }}
    >
      {props.children}
    </DashboardContext.Provider>
  );
};
